/**
 * useReservationStatusChange.ts
 * 予約ステータス・車両ステータス・移動ステータスの即時保存を一元管理するフック
 *
 * 全ページ・全パネルで同じシーケンスで動作。
 * ロジック変更はこのファイルのみで対応。
 *
 * === 予約ステータス変更フロー ===
 * 1. ステータスボタンクリック
 * 2. 「利用中(in_use)」の場合 → チェック初期化確認ダイアログ
 * 3. 初期化した場合 → 車両を「出発中」にするか確認ダイアログ
 * 4. 予約ステータスのみをAPIで即保存（フォーム全体は保存しない）
 *
 * === 車両ステータス変更フロー ===
 * 1. ステータスボタンクリック
 * 2. 「出発中」の場合 → チェック初期化確認ダイアログ
 * 3. 車両ステータスのみをAPIで即保存（フォーム全体は保存しない）
 *
 * === 移動ステータス変更フロー ===
 * 1. ボタンクリック → 即API保存
 */
import { useState, useCallback, useRef } from 'react';
import { saveDispatchReservation } from '../api/dispatchService';
import { updateVehicle } from '../api/vehicleService';

interface StatusOption {
  id: number | string;
  label?: string;
  name?: string;
  code?: string;
  bg_color?: string;
  text_color?: string;
}

/** 確認ダイアログの段階を管理 */
type ConfirmStep = 'check_reset' | 'set_departing' | null;

interface ConfirmDialogState {
  open: boolean;
  message: string;
  yesLabel: string;
  noLabel: string;
  step: ConfirmStep;
}

/** コールバックチェーンを保持 */
interface PendingAction {
  type: 'reservation_status' | 'vehicle_status';
  reservationId?: string;
  vehicleId?: string;
  newStatusId: string | number;
  step: ConfirmStep;
}

interface UseReservationStatusChangeOptions {
  /** 予約ステータス一覧 */
  reservationStatusList?: StatusOption[];
  /** 現在の予約データ取得 */
  getReservationData?: () => any;
  /** 予約ステータス変更後のコールバック */
  onReservationStatusSaved?: (reservationId: string, newStatus: string | number, statusName: string) => void;
  /** 車両ステータス変更後のコールバック */
  onVehicleStatusSaved?: (vehicleId: string, updates: Record<string, any>) => void;
  /** トースト表示用 */
  onToast?: (message: string, severity?: string) => void;
}

const INITIAL_CONFIRM: ConfirmDialogState = {
  open: false,
  message: '',
  yesLabel: '',
  noLabel: '',
  step: null,
};

export function useReservationStatusChange({
  reservationStatusList = [],
  getReservationData,
  onReservationStatusSaved,
  onVehicleStatusSaved,
  onToast,
}: UseReservationStatusChangeOptions) {
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(INITIAL_CONFIRM);
  const [isSaving, setIsSaving] = useState(false);
  const pendingRef = useRef<PendingAction | null>(null);

  // ========================================
  // 内部: 予約ステータスをAPI保存
  // ========================================
  const doSaveReservationStatus = useCallback(
    async (reservationId: string, newStatusId: string | number) => {
      const currentData = getReservationData?.() || {};
      await saveDispatchReservation({
        ...currentData,
        id: reservationId,
        status: newStatusId,
      } as any);
      const statusObj = reservationStatusList.find((s) => String(s.id) === String(newStatusId));
      const statusName = statusObj?.label || statusObj?.name || String(newStatusId);
      onReservationStatusSaved?.(reservationId, newStatusId, statusName);
      return statusName;
    },
    [reservationStatusList, getReservationData, onReservationStatusSaved],
  );

  // ========================================
  // 内部: 車両ステータスをAPI保存
  // ========================================
  const doSaveVehicleStatus = useCallback(
    async (vehicleId: string, updates: Record<string, any>) => {
      await updateVehicle({ id: vehicleId, ...updates });
      onVehicleStatusSaved?.(vehicleId, updates);
    },
    [onVehicleStatusSaved],
  );

  // ========================================
  // 内部: チェック初期化実行
  // ========================================
  const doResetChecks = useCallback(
    async (vehicleId: string) => {
      await updateVehicle({
        id: vehicleId,
        check_situations: [],
        cleaning: false,
        car_wash: false,
        check1: false,
        check2: false,
        tire_groove: false,
        tire_pressure: false,
      });
    },
    [],
  );

  // ========================================
  // 確認ダイアログの応答処理
  // ========================================
  const closeConfirmDialog = useCallback(
    async (result: boolean) => {
      const pending = pendingRef.current;
      setConfirmDialog(INITIAL_CONFIRM);

      if (!pending) return;

      setIsSaving(true);
      try {
        if (pending.step === 'check_reset') {
          // Step 1の回答: チェック初期化するか
          if (result && pending.vehicleId) {
            await doResetChecks(pending.vehicleId);
            onVehicleStatusSaved?.(pending.vehicleId, {
              check_situations: [],
              cleaning: false, car_wash: false,
              check1: false, check2: false,
              tire_groove: false, tire_pressure: false,
            });
          }

          if (pending.type === 'reservation_status') {
            // 予約ステータス保存
            if (pending.reservationId) {
              const statusName = await doSaveReservationStatus(pending.reservationId, pending.newStatusId);
              onToast?.(`ステータスを「${statusName}」に変更しました`, 'success');
            }

            // チェックを初期化した場合 → 車両を「出発中」にするか確認
            if (result && pending.vehicleId) {
              pendingRef.current = {
                ...pending,
                step: 'set_departing',
              };
              setConfirmDialog({
                open: true,
                message: '車両ステータスを「出発中」に変更しますか？',
                yesLabel: 'はい (出発中)',
                noLabel: 'いいえ (そのまま)',
                step: 'set_departing',
              });
              setIsSaving(false);
              return;
            }
          } else if (pending.type === 'vehicle_status') {
            // 車両ステータス保存（出発中への変更）
            if (pending.vehicleId) {
              await doSaveVehicleStatus(pending.vehicleId, { status: Number(pending.newStatusId) });
              onToast?.('車両ステータスを更新しました', 'success');
            }
          }
        } else if (pending.step === 'set_departing') {
          // Step 2の回答: 車両を出発中にするか
          if (result && pending.vehicleId) {
            await doSaveVehicleStatus(pending.vehicleId, { status: 1 }); // 1 = 出発中
            onToast?.('車両ステータスを「出発中」に変更しました', 'success');
          }
        }
      } catch (error) {
        console.error('[useReservationStatusChange] error:', error);
        onToast?.('更新に失敗しました', 'error');
      } finally {
        pendingRef.current = null;
        setIsSaving(false);
      }
    },
    [doSaveReservationStatus, doSaveVehicleStatus, doResetChecks, onVehicleStatusSaved, onToast],
  );

  // ========================================
  // 予約ステータス変更リクエスト
  // ========================================
  const requestStatusChange = useCallback(
    async (
      reservationId: string,
      newStatusId: string | number,
      currentStatus: string | number | null,
      vehicleId?: string | null,
    ) => {
      const statusObj = reservationStatusList.find((s) => String(s.id) === String(newStatusId));
      const statusCode = statusObj?.code || '';

      // 「利用中(in_use)」への変更 → チェック初期化確認
      if (statusCode === 'in_use' && vehicleId) {
        pendingRef.current = {
          type: 'reservation_status',
          reservationId,
          vehicleId,
          newStatusId,
          step: 'check_reset',
        };
        setConfirmDialog({
          open: true,
          message: 'チェックを初期化しますか？',
          yesLabel: 'はい (初期化)',
          noLabel: 'いいえ (そのまま)',
          step: 'check_reset',
        });
        return;
      }

      // それ以外は即保存
      setIsSaving(true);
      try {
        const statusName = await doSaveReservationStatus(reservationId, newStatusId);
        onToast?.(`ステータスを「${statusName}」に変更しました`, 'success');
      } catch (error) {
        console.error('[useReservationStatusChange] save error:', error);
        onToast?.('ステータスの更新に失敗しました', 'error');
      } finally {
        setIsSaving(false);
      }
    },
    [reservationStatusList, doSaveReservationStatus, onToast],
  );

  // ========================================
  // 車両ステータス変更リクエスト
  // ========================================
  const requestVehicleStatusChange = useCallback(
    async (vehicleId: string, newStatusId: number, currentStatus?: number) => {
      // 「出発中」(1) への変更 → チェック初期化確認
      if (newStatusId === 1 && currentStatus !== 1) {
        pendingRef.current = {
          type: 'vehicle_status',
          vehicleId,
          newStatusId,
          step: 'check_reset',
        };
        setConfirmDialog({
          open: true,
          message: 'チェックを初期化しますか？',
          yesLabel: 'はい (初期化)',
          noLabel: 'いいえ (そのまま)',
          step: 'check_reset',
        });
        return;
      }

      // それ以外は即保存
      setIsSaving(true);
      try {
        await doSaveVehicleStatus(vehicleId, { status: newStatusId });
        onToast?.('車両ステータスを更新しました', 'success');
      } catch (error) {
        console.error('[useReservationStatusChange] vehicle status save error:', error);
        onToast?.('更新に失敗しました', 'error');
      } finally {
        setIsSaving(false);
      }
    },
    [doSaveVehicleStatus, onToast],
  );

  // ========================================
  // 移動ステータス変更リクエスト（即保存、確認不要）
  // ========================================
  const requestLocationStatusChange = useCallback(
    async (vehicleId: string, newLocationStatusId: number | null) => {
      setIsSaving(true);
      try {
        await updateVehicle({
          id: vehicleId,
          location_status: newLocationStatusId,
        });
        onVehicleStatusSaved?.(vehicleId, { location_status: newLocationStatusId });
        onToast?.('移動ステータスを更新しました', 'success');
      } catch (error) {
        console.error('[useReservationStatusChange] location status save error:', error);
        onToast?.('更新に失敗しました', 'error');
      } finally {
        setIsSaving(false);
      }
    },
    [onVehicleStatusSaved, onToast],
  );

  return {
    /** 予約ステータス変更 */
    requestStatusChange,
    /** 車両ステータス変更 */
    requestVehicleStatusChange,
    /** 移動ステータス変更 */
    requestLocationStatusChange,
    /** 確認ダイアログ */
    confirmDialog,
    closeConfirmDialog,
    /** 保存中フラグ */
    isSaving,
  };
}
